/**
 * API configuration for the application
 * 
 * This file provides the base URL configuration for API requests.
 * In production, we use relative URLs that work with the domain from which the app is served.
 * In development, we can use an absolute URL with localhost if needed.
 */
import axios from 'axios';

/**
 * Returns the base URL for API requests
 * Using a relative URL works both in development and production environments
 */
export const getApiBaseUrl = (): string => {
  // Use absolute URL in development since backend runs on different port
  if (process.env.NODE_ENV === 'development' || window.location.port === '3000') {
    return 'http://localhost:8000/api';
  }
  // Use relative URL in production
  return '/api';
};

/**
 * Helper function to build a complete API URL
 */
export const getApiUrl = (endpoint: string): string => {
  // Ensure endpoint doesn't start with a slash if we're appending it to the base URL
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
  // Return the complete API URL
  return `${getApiBaseUrl()}/${normalizedEndpoint}`;
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Get the authentication token from localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

export default api;